import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  OnDestroy,
  Output,
  signal,
} from '@angular/core';
import { fromEvent, Subject, Subscription, zip } from 'rxjs';
import { delay, filter, tap, throttleTime, withLatestFrom } from 'rxjs/operators';

@Directive()
export abstract class ScrollableLineDirective implements AfterViewInit, OnDestroy {
  resetScrollAxis: Subject<any>;

  @Output()
  resetAllScrollAxis = new EventEmitter();

  @HostBinding('class.resetting')
  resetting = false;

  scrollDirectionLocked: 'vertical' | 'horizontal' = null;
  $xAxis = signal(0);
  $prevAxis = signal(0);
  iconsNumber = 1;
  iconSize = 60;
  maxWidth = 767;

  subscription = new Subscription();

  constructor(protected readonly host: ElementRef) {}

  ngAfterViewInit(enabled = true) {
    if (enabled) {
      this.subscription.add(
        zip(
          fromEvent<TouchEvent>(this.host.nativeElement, 'touchmove').pipe(
            withLatestFrom(fromEvent<TouchEvent>(this.host.nativeElement, 'touchstart')),
          ),
        )
          .pipe(
            filter(() => window.innerWidth <= this.maxWidth),
            tap(([[touchMoveEvent, touchstartEvent]]) => {
              if (touchMoveEvent.touches && touchstartEvent.touches) {
                const xDiffX =
                  touchMoveEvent.touches[0].clientX - touchstartEvent.touches[0].clientX;
                const xDiffY =
                  touchMoveEvent.touches[0].clientY - touchstartEvent.touches[0].clientY;
                // Vertical scroll
                if (
                  Math.abs(xDiffY) > 1 &&
                  Math.abs(xDiffX) < 5 &&
                  this.scrollDirectionLocked !== 'horizontal'
                ) {
                  this.$xAxis.set(0);
                  this.scrollDirectionLocked = 'vertical';
                  this.resetAllScrollAxis.emit();
                  return;
                }

                // Horizontal scroll
                if (this.scrollDirectionLocked !== 'vertical') {
                  touchMoveEvent.stopPropagation();
                  this.scrollDirectionLocked = 'horizontal';
                  let moveValue = Math.max(
                    0,
                    Math.min(this.iconSize * this.iconsNumber, xDiffX),
                  );
                  if (this.$prevAxis() === 0) {
                    moveValue = Math.max(
                      -this.iconSize * this.iconsNumber,
                      Math.min(0, xDiffX),
                    );
                  }
                  this.$xAxis.set(this.$prevAxis() + Math.round(moveValue));
                }
              }
            }),
          )
          .subscribe(),
      );
      this.subscription.add(
        fromEvent<TouchEvent>(this.host.nativeElement, 'touchend').subscribe(() => {
          this.scrollDirectionLocked = null;
          if (this.$xAxis() < -((this.iconSize * this.iconsNumber) / 2)) {
            this.$xAxis.set(-this.iconSize * this.iconsNumber);
          } else {
            this.$xAxis.set(0);
          }
          this.$prevAxis.set(this.$xAxis());
        }),
      );
      this.subscription.add(
        this.resetScrollAxis
          .pipe(
            throttleTime(1000),
            tap(() => {
              this.resetting = true;
              this.$xAxis.set(0);
            }),
            delay(200),
            tap(() => {
              this.resetting = false;
            }),
          )
          .subscribe(),
      );
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
